import type { ReactNode } from "react";
import { cn } from "../../../lib/utils";

interface MapEditorPaneHeaderProps {
  title: string;
  description?: string | null;
  icon?: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
  className?: string;
  titleClassName?: string;
}

export function MapEditorPaneHeader({
  title,
  description,
  icon,
  badge,
  actions,
  className,
  titleClassName,
}: MapEditorPaneHeaderProps) {
  return (
    <div className={cn("flex items-start justify-between gap-3", className)}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          {icon ? <div className="shrink-0 text-primary">{icon}</div> : null}
          <h3 className={cn("truncate text-sm font-black tracking-tight text-foreground", titleClassName)}>
            {title}
          </h3>
          {badge ? <div className="shrink-0">{badge}</div> : null}
        </div>
        {description ? (
          <p className="text-[11px] leading-4 text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="shrink-0">{actions}</div> : null}
    </div>
  );
}

interface MapEditorEmptyStateProps {
  title?: string;
  message: string;
  icon?: ReactNode;
  className?: string;
}

export function MapEditorEmptyState({
  title,
  message,
  icon,
  className,
}: MapEditorEmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/20 px-5 py-8 text-center",
        className
      )}
    >
      {icon ? (
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background text-muted-foreground shadow-sm">
          {icon}
        </div>
      ) : null}
      {title ? (
        <div className="text-xs font-semibold text-foreground">{title}</div>
      ) : null}
      <p className="max-w-sm text-[11px] leading-5 text-muted-foreground">
        {message}
      </p>
    </div>
  );
}

export interface MapEditorSummaryRowItem {
  key: string;
  label: string;
  value: ReactNode;
}

interface MapEditorSummaryListProps {
  items: MapEditorSummaryRowItem[];
  className?: string;
  rowClassName?: string;
  valueClassName?: string;
  dense?: boolean;
}

export function MapEditorSummaryList({
  items,
  className,
  rowClassName,
  valueClassName,
  dense = false,
}: MapEditorSummaryListProps) {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-border bg-background", className)}>
      {items.map((item, index) => (
        <div
          key={item.key}
          className={cn(
            "flex items-center justify-between gap-3",
            dense ? "px-3 py-2" : "px-3 py-2.5",
            index > 0 && "border-t border-border",
            rowClassName
          )}
        >
          <span className="min-w-0 text-[10px] font-bold uppercase tracking-tight text-muted-foreground">
            {item.label}
          </span>
          <span
            className={cn(
              "shrink-0 text-[11px] font-mono font-semibold text-foreground",
              valueClassName
            )}
          >
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
}
